import { useEffect, useMemo, useState } from 'react';
import {
  Credenza,
  CredenzaBody,
  CredenzaClose,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
  CredenzaTrigger,
} from '@/components/ui/credenza';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DeviceTypeCodes,
  DeviceTypeNames,
} from '@/features/devices/schemas/devices.schemas.ts';
import {
  DEVICE_FIELDS_METADATA,
  getDeviceTypeIdByName,
} from '@/features/devices/schemas/devices.metadata.ts';
import { Input } from '@/components/ui/input.tsx';
import { LoaderIcon, SquarePen } from 'lucide-react';
import { useUpdatePlantOverviewConfig } from '@/features/plants/hooks/mutations/[plantId]/use-update-plant-overview-config';

type DeviceField = {
  id: number;
  name: string;
  unit: string;
  decimals: number;
};

type DeviceTypeFields = {
  deviceTypeCode: DeviceTypeCodes;
  deviceTypeName: string;
  fields: DeviceField[];
};

type DeviceFieldsPickerDialogProps = {
  title?: string;
  plantId: string;
  config?: Partial<Record<DeviceTypeCodes, number[]>>;
  type: 'production' | 'storage';
  deviceTypes?: DeviceTypeNames[];
};

export const DeviceFieldsPickerDialog = ({
  title,
  plantId,
  config,
  type,
  deviceTypes,
}: DeviceFieldsPickerDialogProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFieldsByDeviceType, setSelectedFieldsByDeviceType] = useState<
    Map<DeviceTypeCodes, Set<number>>
  >(new Map());

  useEffect(() => {
    if (!config) return;

    const initialMap = new Map<DeviceTypeCodes, Set<number>>();

    Object.entries(config).forEach(([deviceTypeCode, fieldIds]) => {
      if (fieldIds && fieldIds.length > 0) {
        initialMap.set(Number(deviceTypeCode) as DeviceTypeCodes, new Set(fieldIds));
      }
    });

    setSelectedFieldsByDeviceType(initialMap);
  }, [config]);

  const availableDevicesFields = useMemo<DeviceTypeFields[]>(() => {
    if (!deviceTypes || deviceTypes.length === 0) return [];

    return deviceTypes
      .map((deviceTypeName) => {
        const deviceTypeCode = getDeviceTypeIdByName(deviceTypeName);
        const fields = Object.values(
          DEVICE_FIELDS_METADATA[deviceTypeCode].parametersByCode
        ) as DeviceField[];

        return {
          deviceTypeCode,
          deviceTypeName,
          fields,
        };
      })
      .filter((item) => item.fields.length > 0);
  }, [deviceTypes]);

  const filteredDevicesFields = useMemo(() => {
    if (!searchQuery) return availableDevicesFields;

    const query = searchQuery.toLowerCase();
    return availableDevicesFields
      .map((deviceType) => ({
        ...deviceType,
        fields: deviceType.fields.filter((field) => field.name.toLowerCase().includes(query)),
      }))
      .filter((deviceType) => deviceType.fields.length > 0);
  }, [availableDevicesFields, searchQuery]);

  const handleToggleField = (deviceTypeCode: DeviceTypeCodes, fieldId: number) => {
    setSelectedFieldsByDeviceType((prev) => {
      const newMap = new Map(prev);
      const deviceFields = newMap.get(deviceTypeCode) || new Set<number>();
      const newSet = new Set(deviceFields);

      if (newSet.has(fieldId)) {
        newSet.delete(fieldId);
      } else {
        newSet.add(fieldId);
      }

      if (newSet.size === 0) {
        newMap.delete(deviceTypeCode);
      } else {
        newMap.set(deviceTypeCode, newSet);
      }

      return newMap;
    });
  };

  const handleSelectAllForDeviceType = (
    deviceTypeCode: DeviceTypeCodes,
    fields: DeviceField[]
  ) => {
    setSelectedFieldsByDeviceType((prev) => {
      const newMap = new Map(prev);
      newMap.set(deviceTypeCode, new Set(fields.map((f) => f.id)));
      return newMap;
    });
  };

  const handleDeselectAllForDeviceType = (deviceTypeCode: DeviceTypeCodes) => {
    setSelectedFieldsByDeviceType((prev) => {
      const newMap = new Map(prev);
      newMap.delete(deviceTypeCode);
      return newMap;
    });
  };

  const { mutate, isPending } = useUpdatePlantOverviewConfig({
    onSuccess: () => {
      setOpen(false);
    },
  });

  const handleSave = () => {
    const config = Object.fromEntries(
      Array.from(selectedFieldsByDeviceType.entries()).map(([deviceTypeCode, fieldIds]) => [
        deviceTypeCode,
        Array.from(fieldIds),
      ])
    ) as Partial<Record<DeviceTypeCodes, number[]>>;

    mutate({
      plantId,
      type,
      config,
    });
  };

  const isFieldSelected = (deviceTypeCode: DeviceTypeCodes, fieldId: number) => {
    return selectedFieldsByDeviceType.get(deviceTypeCode)?.has(fieldId) ?? false;
  };

  const getSelectedCountForDeviceType = (deviceTypeCode: DeviceTypeCodes) => {
    return selectedFieldsByDeviceType.get(deviceTypeCode)?.size ?? 0;
  };

  if (!deviceTypes || deviceTypes.length === 0) return null;

  return (
    <Credenza open={open} onOpenChange={setOpen}>
      <CredenzaTrigger asChild>
        <Button variant="outline">
          <SquarePen className="size-5" />
        </Button>
      </CredenzaTrigger>
      <CredenzaContent className="max-w-2xl">
        <CredenzaHeader>
          <CredenzaTitle>Select {title ? title : `Table`} Fields</CredenzaTitle>
          <CredenzaDescription>
            Choose which device fields to display in the table.
          </CredenzaDescription>
        </CredenzaHeader>

        <CredenzaBody className="px-1 sm:px-0">
          <div className="space-y-4">
            <div className="px-3 sm:px-0">
              <Input
                placeholder="Search fields..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9"
              />
            </div>

            <ScrollArea className="h-[500px]">
              <div className="space-y-6 px-3 sm:px-0">
                {filteredDevicesFields.map((deviceType) => (
                  <div key={deviceType.deviceTypeCode} className="space-y-3">
                    <div className="bg-background sticky top-0 flex items-center justify-between border-b py-2">
                      <div className="flex items-center gap-2 pl-1">
                        <h3 className="text-sm font-semibold">{deviceType.deviceTypeName}</h3>
                        <span className="text-muted-foreground text-xs">
                          ({getSelectedCountForDeviceType(deviceType.deviceTypeCode)}/
                          {deviceType.fields.length})
                        </span>
                      </div>
                      <div className="flex gap-2 sm:pr-4">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() =>
                            handleSelectAllForDeviceType(
                              deviceType.deviceTypeCode,
                              deviceType.fields
                            )
                          }
                          className="h-7 text-xs"
                        >
                          All
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() =>
                            handleDeselectAllForDeviceType(deviceType.deviceTypeCode)
                          }
                          className="h-7 text-xs"
                        >
                          None
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {deviceType.fields.map((field) => (
                        <div
                          key={`${deviceType.deviceTypeCode}-${field.id}`}
                          className="hover:bg-accent flex items-start space-x-3 rounded-md p-3 transition-colors"
                        >
                          <Checkbox
                            id={`field-${deviceType.deviceTypeCode}-${field.id}`}
                            checked={isFieldSelected(deviceType.deviceTypeCode, field.id)}
                            onCheckedChange={() => {
                              handleToggleField(deviceType.deviceTypeCode, field.id);
                            }}
                            className="mt-0.5 cursor-pointer"
                          />
                          <Label
                            htmlFor={`field-${deviceType.deviceTypeCode}-${field.id}`}
                            className="group flex-1 cursor-pointer text-sm leading-none font-medium"
                          >
                            <div className="flex flex-col gap-1 group-not-has-[p]:mt-0.5">
                              <span title={String(field.id)}>{field.name}</span>
                              {field.unit && (
                                <p className="text-muted-foreground text-xs">
                                  Unit: {field.unit}
                                </p>
                              )}
                            </div>
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {filteredDevicesFields.length === 0 && (
                  <div className="text-muted-foreground py-8 text-center">
                    No fields found matching "{searchQuery}"
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </CredenzaBody>

        <CredenzaFooter>
          <CredenzaClose asChild>
            <Button variant="outline">Cancel</Button>
          </CredenzaClose>
          <Button disabled={isPending} onClick={handleSave}>
            {isPending && (
              <LoaderIcon className="mr-1.5 size-4 animate-spin" aria-hidden="true" />
            )}
            Save Changes
          </Button>
        </CredenzaFooter>
      </CredenzaContent>
    </Credenza>
  );
};
